/**
 * VoicingEngine.js
 * Implements "Vertical" enhancements for Advanced Orchestral Voicing.
 * Handles Low Interval Limits, Drop-2 Voicings, Doubling Rules, and Pedal Points.
 */

import { VOICING_LIMITS } from '../../data/StrategyConfig.js';

// Low Interval Limits (Semitones from C0)
// To prevent mud, intervals below these pitches must be 5ths or Octaves
const LOW_INTERVAL_LIMIT = VOICING_LIMITS.lowIntervalLimit || 48; // C3

// Clash Avoidance Limit
// Notes closer than a semitone to the melody range (usually > 72) must be filtered unless intentional
const MELODY_CLASH_THRESHOLD = VOICING_LIMITS.melodyClashThreshold || 72;

const VOICING_CONFIG = {
    doubling: {
        power: { intervals: [-12, -7] },
        cinematic: { intervals: [-12, -7] },
        standard: { intervals: [-12] },
        pastoral: { intervals: [3, 4, -9, -8] }, // 3rds/6ths (approx)
        gothic_thick: { intervals: [-12, -24, 7] }
    }
};

/**
 * Distributes chord tones to abstract roles to ensure full harmony.
 * @param {Array<number>} chordNotes - Full set of chord notes (MIDI).
 * @param {string} role - The role requesting notes ('bass', 'pad', 'strings').
 * @returns {Array<number>} Subset of notes best suited for the role.
 */
export const distributeVoices = (chordNotes, role) => {
    if (!chordNotes || chordNotes.length === 0) return [];

    // Sort Low to High
    const sorted = [...chordNotes].sort((a,b) => a-b);
    const root = sorted[0];
    const top = sorted[sorted.length-1];

    // Calculate Intervals from Root
    const intervals = sorted.map(n => (n - root) % 12);

    // Identify Tones
    // 0=Root, 7=5th, 3/4=3rd, 10/11=7th, 2/14=9th
    const tones = {
        root: sorted.filter(n => (n-root)%12 === 0),
        third: sorted.filter(n => (n-root)%12 === 3 || (n-root)%12 === 4),
        fifth: sorted.filter(n => (n-root)%12 === 7),
        seventh: sorted.filter(n => (n-root)%12 === 10 || (n-root)%12 === 11),
        extensions: sorted.filter(n => ![0,3,4,7,10,11].includes((n-root)%12))
    };

    if (role === 'bass') {
        return tones.root; // Bass takes Roots
    }
    else if (role === 'pad' || role === 'harmony') {
        // Pads take Guide Tones (3rd, 7th) for color
        const guides = [...tones.third, ...tones.seventh];
        if (guides.length > 0) return guides;
        return [root + 7]; // Fallback to 5th if no guide tones
    }
    else if (role === 'strings' || role === 'counter_melody') {
        // Strings take 5th and Extensions (9th, 11th) or Top Note
        const color = [...tones.fifth, ...tones.extensions];
        if (color.length > 0) return color;
        return [top];
    }

    return sorted; // Default: All notes
};

/**
 * Applies Instrument-Aware Voicing based on physical/ensemble constraints.
 * Phase 6 Enhancement.
 * @param {Array<number>} notes - Raw MIDI notes.
 * @param {Object} instrument - Instrument definition.
 */
export const applyInstrumentVoicing = (notes, instrument) => {
    if (!notes || notes.length === 0) return [];
    if (!instrument) return notes;

    let voiced = [...notes];

    // --- SPECTRAL BINNING ---
    // If instrument has assigned Spectral Zone, force wrap into it
    // Check instrument.range for strict zones (from RoleAllocator)
    if (instrument.range && (instrument.range.min === 54 || instrument.range.min === 24 || instrument.range.min === 71)) {
        // Simple heuristic: If range matches strict bins
        const min = instrument.range.min;
        const max = instrument.range.max;

        // Check center of gravity
        let sum = 0;
        voiced.forEach(n => sum += n);
        let avg = sum / voiced.length;

        // Shift until average is within zone
        let shifts = 0;
        while (avg < min && shifts < 5) {
            voiced = voiced.map(n => n + 12);
            avg += 12;
            shifts++;
        }
        shifts = 0;
        while (avg > max && shifts < 5) {
            voiced = voiced.map(n => n - 12);
            avg -= 12;
            shifts++;
        }
    }

    // 1. GUITAR LOGIC (Fretboard Constraints)
    // If instrument is guitar-like, enforce max 6 strings and playable stretches.
    const isGuitar = instrument.id.includes('guitar') || (instrument.name && instrument.name.toLowerCase().includes('guitar'));
    if (isGuitar) {
        // Ensure sorted before processing
        voiced.sort((a,b) => a-b);

        // Limit to 6 notes
        if (voiced.length > 6) {
            // Keep lowest 6 notes
            voiced = voiced.slice(0, 6);
        }

        // Stretch Check: If range > 2 octaves (24 semitones) without open strings, it's impossible.
        // Simplified: If range > 17 semitones (approx 12 frets + string crossing), compress.
        const stretchLimit = VOICING_LIMITS.guitarStretch || 19;
        const range = voiced[voiced.length-1] - voiced[0];

        if (range > stretchLimit) { // Very wide
            // Compact: Move top notes down or bottom notes up
            // Move lowest up an octave
            const lowest = voiced.shift();
            voiced.push(lowest + 12);
            voiced.sort((a,b) => a-b);
        }
    }

    // Sort before returning if not guitar, or if guitar processing didn't sort
    voiced.sort((a,b) => a-b);

    // 2. ORCHESTRAL DIVISI / SOLO LOGIC
    // Check if Solo instrument (sectionSize = 1 or explicit 'solo' tag)
    // Assuming instrument.sectionSize exists (from RoleAllocator) or name implies solo
    const isSolo = (instrument.sectionSize === 1) || instrument.id.includes('solo') || instrument.name?.toLowerCase().includes('solo');

    if (isSolo) {
        // Solo instruments usually monophonic or limited double stops (Strings)
        const isStrings = instrument.id.includes('violin') || instrument.id.includes('cello') || instrument.id.includes('viola');

        if (isStrings) {
            // Max 2 notes (Double stop)
            if (voiced.length > 2) {
                // Keep Top and Bottom (Shell)
                const top = voiced[voiced.length-1];
                const bottom = voiced[0];
                voiced = [bottom, top];
            }
            // Check interval for double stop (max 12th usually, but safe limit 5th/6th/octave)
            if (voiced.length === 2 && (voiced[1] - voiced[0]) > 14) {
                // Too wide, drop bottom
                voiced = [voiced[1]];
            }
        } else {
            // Wind/Brass Solo -> Monophonic
            // Keep Highest Note (Melody) or Root? Usually Top for voice leading.
            if (voiced.length > 1) {
                voiced = [voiced[voiced.length-1]];
            }
        }
    } else {
        // ENSEMBLE / SECTION DIVISI
        // If we have a section size, we can split notes?
        // Actually, RoleAllocator handles 'smart_divisi' strategy which calls applyInstrumentVoicing individually?
        // No, adaptEvents calls this.

        // If we have 4 notes and sectionSize is defined (e.g. 4 horns), we assume 'tutti' or 'divisi' is handled by role assignment.
        // But if a single instrument object represents a "Section" (e.g. "Violins 1"), it can play polyphony (Divisi).
        // No restriction needed here, except ensuring we don't have 10 notes for a 4-person section.
        if (instrument.sectionSize && voiced.length > instrument.sectionSize) {
            // Limit polyphony to section size
            // Keep most important tones: Root, 3rd, 7th, Top
            // For now, just slice top N
            // Better: Slice to keep spread
            const surplus = voiced.length - instrument.sectionSize;
            // Drop inner voices?
            // Simple: Top N
            voiced = voiced.slice(surplus);
        }
    }

    // 3. KEYBOARD IDIOMS (Jazz Shells)
    const isKeys = instrument.id.includes('piano') || instrument.id.includes('keys');
    const isJazz = false; // TODO: Context injection needed for Genre-specific idioms. Assuming standard for now.

    // For now, return validated notes
    return voiced.sort((a,b) => a-b);
};

/**
 * Applies Advanced Orchestral Voicing strategies to a set of notes.
 * @param {Array<number>} notes - Array of MIDI note numbers.
 * @param {Object} instrument - The instrument definition object.
 * @param {string} role - The role of the instrument (e.g., 'pad', 'brass').
 * @param {string} strategy - 'classic', 'power', 'cinematic' (default: 'classic')
 * @param {Object} config - (Optional) Seeded configuration object
 * @returns {Array<number>} - The re-voiced notes.
 */
export const applyOrchestralVoicing = (notes, instrument, role, strategy = 'classic', config = null) => {
    if (!notes || notes.length === 0) return [];

    // Sort notes for consistent processing
    let sortedNotes = [...notes].sort((a, b) => a - b);

    // 0. ROLE-BASED VOICING (New: Distribute Voices)
    // If role is specific (Bass, Pad, Strings), filter notes to avoid unison clutter.
    // Only apply if notes > 1 (polyphonic context)
    if (notes.length > 2 && (role === 'bass' || role === 'pad' || role === 'strings')) {
        const distributed = distributeVoices(sortedNotes, role);
        // If distribution leaves us with empty (e.g. no 7th for Pad), fallback to original
        if (distributed.length > 0) {
            sortedNotes = distributed;
        }
    }

    // 1. LOW INTERVAL LIMIT CHECK (Mud Filter)
    // Always run this to clean up chords generated by SATB or random logic
    sortedNotes = enforceLowIntervalLimits(sortedNotes);

    // 2. CLASH FILTER (High Register)
    // Ensure high harmony notes don't clutter the melody range with dissonance
    if (role !== 'melody') {
        sortedNotes = filterClashes(sortedNotes);
    }

    // 2. BRASS / DROP-2 LOGIC
    // Use Traits: Family = Brass
    // Wiring up Ghost Variable: drop2Chance
    let drop2Chance = (config && config.voicing) ? config.voicing.drop2Chance : 1.0;

    // Scale chance based on section intensity if available in config
    if (config && config.intensity > 0.8) drop2Chance *= 0.5; // Dense sections prefer close voicing

    if (hasTrait(instrument, 'family', 'brass') && sortedNotes.length >= 4) {
        if (Math.random() < drop2Chance) {
            sortedNotes = applyDrop2(sortedNotes);
        }
    }

    // ORGANUM LOGIC (Medieval)
    if (strategy === 'organum') {
        sortedNotes = applyOrganumVoicing(sortedNotes);
    }

    // 3. DOUBLING RULES
    // Use Traits: Section = Low + Orchestral
    const isLowOrchestral = hasTrait(instrument, 'section', 'low') && hasTrait(instrument, 'orchestral', true);

    // Probabilistic Doubling (Dynamic Intensity)
    // Scale doubling chance with Intensity (if provided via config or context)
    // We assume 'config' might have 'intensity' injected, or we default to random
    let doublingChance = (config && config.voicing) ? config.voicing.doublingChance : 0.5;

    // Dynamic Boost: High Intensity -> High Doubling Chance
    // We assume context.intensity is passed down, but here we only have 'config'.
    // If config has intensity (GenerationPipeline often injects), use it.
    if (config && typeof config.intensity === 'number') {
        doublingChance = 0.2 + (config.intensity * 0.8); // 0.2 to 1.0
    }

    const shouldDouble = Math.random() < doublingChance;

    if ((role === 'bass' || isLowOrchestral) && shouldDouble) {
        // Use strategy from config if it exists, otherwise default
        if (VOICING_CONFIG.doubling[strategy]) {
            sortedNotes = applyConfigurableDoubling(sortedNotes, instrument, VOICING_CONFIG.doubling[strategy].intervals);
        } else {
            sortedNotes = applyConfigurableDoubling(sortedNotes, instrument, VOICING_CONFIG.doubling.standard.intervals);
        }
    }

    // 4. TUTTI DOUBLING (High Strings/Woodwinds)
    const isHighOrchestral = (hasTrait(instrument, 'family', 'strings') || hasTrait(instrument, 'family', 'woodwinds')) && hasTrait(instrument, 'section', 'high');

    if (role === 'melody' && isHighOrchestral) {
        if (strategy === 'pastoral') {
             sortedNotes = applyPastoralDoubling(sortedNotes);
        } else {
             sortedNotes = applyMelodyDoubling(sortedNotes);
        }
    }

    return [...new Set(sortedNotes)].sort((a, b) => a - b);
};

// --- NEW: Pedal Point Logic ---
/**
 * Modifies the chord progression to enforce pedal points in specific sections.
 * @param {Array} progression - The full chord progression.
 * @param {string} globalKey - Root key (e.g., 'C').
 */
export const applyPedalPoint = (progression, globalKey) => {
    if (!progression) return [];

    // Parse key to MIDI root
    const keyIdx = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"].indexOf(globalKey);
    const tonicPedal = (keyIdx !== -1) ? keyIdx : 0;
    const dominantPedal = (tonicPedal + 7) % 12;

    return progression.map(chord => {
        let newRoot = chord.rootPitchClass;

        // Intro/Outro -> Tonic Pedal
        if (chord.section === 'INTRO' || chord.section === 'OUTRO') {
            newRoot = tonicPedal;
        }
        // Build/Tension -> Dominant Pedal
        else if (chord.section === 'BUILD' || chord.section === 'PRE_CHORUS') {
            newRoot = dominantPedal;
        }

        // If modified, update chord object
        if (newRoot !== chord.rootPitchClass) {
            return { ...chord, rootPitchClass: newRoot, isPedal: true };
        }

        return chord;
    });
};

/**
 * Applies Internal Pedal (Inner Voice Sustain).
 * Forces an inner voice to hold a common tone if one exists between current and next chord.
 */
export const applyInternalPedal = (progression) => {
    // This requires analyzing adjacent chords.
    // NOTE: This modifies 'notes' in place or requires re-voicing logic that isn't fully exposed here.
    // Instead, we will tag chords with 'internalPedalNote' for the Orchestrator/TextureEngine to use.
    if (!progression || progression.length < 2) return progression;

    return progression.map((chord, i) => {
        if (i === progression.length - 1) return chord;
        const next = progression[i + 1];

        // Find common pitch class
        const currentPCs = (chord.notes && Array.isArray(chord.notes) ? chord.notes : []).map(n => n % 12);
        const nextPCs = (next.notes && Array.isArray(next.notes) ? next.notes : []).map(n => n % 12);
        const common = currentPCs.filter(pc => nextPCs.includes(pc));

        if (common.length > 0) {
            // Pick one (preferably 5th or 1st)
            return { ...chord, internalPedalPC: common[0] };
        }
        return chord;
    });
};

/**
 * Applies Inverted Pedal (High String Sustain).
 * Used for Epic/Cinematic styles.
 */
export const applyInvertedPedal = (progression, rootKey) => {
    const keyIdx = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"].indexOf(rootKey);
    const tonic = keyIdx !== -1 ? keyIdx : 0;
    const dominant = (tonic + 7) % 12;

    return progression.map(chord => {
        // High pedal is usually Dominant (tension) or Tonic (stability)
        // If section is High Tension, use Dominant
        let pedalPC = tonic;
        if (chord.section === 'BUILD' || chord.section === 'CLIMAX') {
            pedalPC = dominant;
        }
        return { ...chord, invertedPedalPC: pedalPC };
    });
};

// Helper: Check for trait existence and value
const hasTrait = (inst, traitKey, value) => {
    return inst.traits && inst.traits[traitKey] === value;
};

/**
 * Enforces Low Interval Limits.
 * Raises non-perfect intervals (3rds, 7ths, 2nds) if they fall below C3 (48).
 */
const enforceLowIntervalLimits = (notes) => {
    // If empty or single note, safe
    if (notes.length <= 1) return notes;

    const cleanNotes = [];
    const root = notes[0];
    cleanNotes.push(root); // Assume root is valid (or enforced by range limits elsewhere)

    for (let i = 1; i < notes.length; i++) {
        let note = notes[i];

        // While note is below limit, check interval
        // If it's a muddy interval (not 5th/8ve), bump octave
        while (note < LOW_INTERVAL_LIMIT) {
            const interval = (note - root) % 12;
            if (interval === 0 || interval === 7) {
                // Safe
                break;
            } else {
                // Muddy (3rd, 6th, 2nd, etc)
                note += 12;
            }
        }
        cleanNotes.push(note);
    }
    return cleanNotes.sort((a,b) => a - b);
};

/**
 * Filter Clashes.
 * Removes semitone dissonances in the high register that might clash with a generic melody.
 */
const filterClashes = (notes) => {
    if (notes.length <= 1) return notes;
    const clean = [];

    for (let i = 0; i < notes.length; i++) {
        const note = notes[i];
        let clash = false;

        // Check against previously accepted notes
        for (let j = 0; j < clean.length; j++) {
            const existing = clean[j];
            const diff = Math.abs(note - existing);

            // STRICT CLASH FILTER: Remove Semitones (1) AND Minor 9ths (13)
            // Unless it's a specific Jazz/Horror context (handled by caller passing strategy, but here we assume general Orchestral)
            if (diff === 1 || diff === 13) {
                 // Prefer Lower Note (Root/5th usually lower) over higher extension
                 // Since we iterate sorted (if sorted), lower is already in 'clean'.
                 // If input is sorted, 'existing' is lower. We drop 'note'.
                 clash = true;
                 break;
            }
        }

        if (!clash) clean.push(note);
    }
    return clean;
};

/**
 * Applies Drop-2 Voicing.
 */
const applyDrop2 = (notes) => {
    if (notes.length < 2) return notes;
    const sorted = [...notes].sort((a, b) => a - b);
    const secondHighestIndex = sorted.length - 2;
    const noteToDrop = sorted[secondHighestIndex];
    sorted.splice(secondHighestIndex, 1);
    sorted.push(noteToDrop - 12);
    return sorted.sort((a, b) => a - b);
};

/**
 * Applies Configurable Doubling based on interval offsets.
 */
const applyConfigurableDoubling = (notes, instrument, intervals) => {
    const result = [];
    notes.forEach(note => {
        result.push(note);
        intervals.forEach(interval => {
            const extra = note + interval;
            // Check range safety
            if (instrument.range && extra >= instrument.range.min && extra <= instrument.range.max) {
                 // Check mud safety if low
                 if (extra < LOW_INTERVAL_LIMIT) {
                     // Only allow 8ves/5ths
                     const rel = Math.abs(interval);
                     if (rel % 12 === 0 || rel % 12 === 7) result.push(extra);
                 } else {
                     result.push(extra);
                 }
            }
        });
    });
    return result;
};

/**
 * Applies Pastoral Doubling (3rds/6ths).
 * Actually adds harmony notes, not just parallel transposition, ideally diatonic.
 * For procedural simplicity, we add parallel 6ths below (which implies inverted 3rds).
 */
const applyPastoralDoubling = (notes) => {
    const result = [];
    notes.forEach(note => {
        result.push(note);
        // Add 6th below (approx -9 semitones, adjusted to scale would be better but parallel 6ths work for effect)
        // 9 semitones = Major 6th. 8 semitones = Minor 6th.
        // We'll alternate or pick Major 6th for brightness?
        result.push(note - 9);
    });
    return result;
};

/**
 * Applies Melody Doubling.
 */
const applyMelodyDoubling = (notes) => {
    const result = [];
    notes.forEach(note => {
        result.push(note);
        result.push(note + 12);
    });
    return result;
};

export const applyHornFifths = (notes) => {
    if (notes.length === 0) return [];
    const sorted = [...notes].sort((a,b) => a-b);
    const top = sorted[sorted.length-1];
    const fifth = top - 7;
    return [fifth, top];
};

/**
 * Applies Organum Voicing (Parallel Perfect Intervals).
 * Used for Medieval/Gregorian styles. Stacks perfect 4ths/5ths/8ves.
 */
export const applyOrganumVoicing = (notes) => {
    if (!notes || notes.length === 0) return [];
    const sorted = [...notes].sort((a, b) => a - b);
    const root = sorted[0]; // Use lowest note as chant base

    // Construct parallel perfect intervals
    const result = [root];
    result.push(root + 7); // Perfect 5th
    result.push(root + 12); // Octave
    result.push(root + 19); // Octave + 5th (Twelfth)

    return result;
};

/**
 * Applies Divisi Strategy.
 * Splits a chord between two sections (e.g., Violin 1 & 2) if both are active.
 * @param {Array<number>} notes - MIDI notes.
 * @param {string} role - 'melody', 'harmony', etc.
 * @param {string} instrumentId - ID of current instrument.
 * @param {Array<string>} activeIds - List of all active instrument IDs.
 */
export const applyDivisiStrategy = (notes, role, instrumentId, activeIds) => {
    if (!notes || notes.length < 2) return notes;

    // Check for Divisi Pairs
    const isViolins = instrumentId.includes('violin');
    const hasV1 = activeIds.some(id => id.includes('violin') && id.includes('1'));
    const hasV2 = activeIds.some(id => id.includes('violin') && id.includes('2'));

    if (isViolins && hasV1 && hasV2) {
        const sorted = [...notes].sort((a,b) => a-b);
        const mid = Math.ceil(sorted.length / 2);

        if (instrumentId.includes('1')) {
            // V1 takes Top Half
            return sorted.slice(mid === 1 ? 1 : mid);
            // If only 2 notes, mid=1. slice(1) gives top note.
            // If 4 notes, mid=2. slice(2) gives top 2.
        } else if (instrumentId.includes('2')) {
            // V2 takes Bottom Half
            return sorted.slice(0, mid === 1 ? 1 : mid);
        }
    }

    return notes;
};

/**
 * Smart Folding: Constrains a note to a range by shifting octaves.
 * Prevents "flatlining" at the top/bottom of ranges.
 * @param {number} note - MIDI note.
 * @param {number} min - Minimum MIDI note.
 * @param {number} max - Maximum MIDI note.
 * @param {number} tolerance - Allowable excursion (semitones) outside strict range.
 * @returns {number} The folded note.
 */
export const constrainToRange = (note, min, max, tolerance = 0) => {
    if (isNaN(note) || typeof note !== 'number') return Math.max(min, 60); // Return safe default
    let folded = note;
    let shifts = 0;
    const MAX_SHIFTS = 8; // Safety Limit to prevent infinite loops

    // Apply Tolerance
    const safeMin = min - tolerance;
    const safeMax = max + tolerance;

    // Shift Up (Wrap)
    while (folded < safeMin && shifts < MAX_SHIFTS) {
        folded += 12;
        shifts++;
    }

    // Shift Down (Wrap)
    shifts = 0;
    while (folded > safeMax && shifts < MAX_SHIFTS) {
        folded -= 12;
        shifts++;
    }

    return folded;
};
